import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { COLORS, type TableViewColumnDto } from '@ditto/shared';
import {
  getColorForOption as getColorForOptionFromCellUtils,
  getColorForOptionValue,
} from '../../utils/cellUtils';
import {
  Calendar,
  Check,
  Hash,
  MessageSquareText,
  Pencil,
  Plus,
  Tag,
  Trash2,
  Type,
} from 'lucide-react';
import { useState, type ChangeEvent, type FC, type ReactNode } from 'react';

export type ColumnType = 'text' | 'date' | 'number' | 'verbatim' | 'options';

export type ColumnOption = {
  id?: string;
  value: string;
};

type CreateColumnModalProps = {
  mode: 'create';
  columns?: TableViewColumnDto[];
  onCreate: (
    name: string,
    prompt: string,
    type: ColumnType,
    options?: ColumnOption[]
  ) => void;
  children?: ReactNode;
};

type EditColumnModalProps = {
  mode: 'edit';
  id: string;
  name: string;
  prompt: string;
  type: ColumnType;
  options?: ColumnOption[];
  columns?: TableViewColumnDto[];
  onEdit: (
    id: string,
    name: string,
    prompt: string,
    type: ColumnType,
    options?: ColumnOption[],
    nameOnly?: boolean
  ) => void;
  children?: ReactNode;
};

export const ColumnModal: FC<
  CreateColumnModalProps | EditColumnModalProps
> = props => {
  const isEdit = props.mode === 'edit';
  const [isOpen, setIsOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [name, setName] = useState(isEdit ? props.name : '');
  const [prompt, setPrompt] = useState(isEdit ? props.prompt : '');
  const [type, setType] = useState<ColumnType>(isEdit ? props.type : 'text');
  const [optionValues, setOptionValues] = useState<string[]>(
    isEdit && props.options ? props.options.map(o => o.value) : []
  );
  // Track which option is being edited (by index), null means none
  const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(
    null
  );
  const [editingValue, setEditingValue] = useState('');

  // Get options sorted alphabetically (excluding empty ones being edited)
  const getSortedOptions = (): ColumnOption[] => {
    return [...optionValues]
      .filter(v => v.trim() !== '')
      .sort((a, b) => a.localeCompare(b))
      .map(value => ({ value }));
  };

  // Get color for an option — delegate to cellUtils for saved options to stay consistent
  const getColorForOption = (value: string): (typeof COLORS)[number] => {
    const keywordColor = getColorForOptionValue(value);
    if (keywordColor) return keywordColor;

    const columns = props.columns ?? [];

    // For edit mode, look up the id from the current column's options first
    const currentColumnOptions = isEdit
      ? ((props as EditColumnModalProps).options ?? [])
      : [];
    const currentOption = currentColumnOptions.find(o => o.value === value);

    if (currentOption?.id && columns.length > 0) {
      return getColorForOptionFromCellUtils(currentOption.id, columns);
    }

    // For new options not yet saved, use local index + offset from existing options
    const allOptions = columns
      .filter(c => c.type === 'options')
      .flatMap(c => c.options ?? []);
    const localIndex = optionValues.indexOf(value);
    return COLORS[(allOptions.length + localIndex) % COLORS.length];
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };
  const handlePromptChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value);
  };

  // Start creating a new option
  const handleCreateNewOption = () => {
    setOptionValues([...optionValues, '']);
    setEditingOptionIndex(optionValues.length);
    setEditingValue('');
  };

  // Start editing an existing option
  const handleStartEditOption = (index: number, currentValue: string) => {
    setEditingOptionIndex(index);
    setEditingValue(currentValue);
  };

  // Save the currently editing option
  const handleSaveOption = () => {
    if (editingOptionIndex === null) return;

    const trimmedValue = editingValue.trim();

    // // Check if empty - remove if it was a new option, cancel if existing
    if (!trimmedValue) {
      // If it's a new option (empty string in array), remove it
      if (optionValues[editingOptionIndex] === '') {
        setOptionValues(
          optionValues.filter((_, i) => i !== editingOptionIndex)
        );
      }
      setEditingOptionIndex(null);
      setEditingValue('');
      return;
    }

    // Check for duplicates (excluding the current option being edited)
    const isDuplicate = optionValues.some(
      (v, i) => i !== editingOptionIndex && v === trimmedValue
    );
    if (isDuplicate) return;

    // Update the option value
    const newOptions = [...optionValues];
    newOptions[editingOptionIndex] = trimmedValue;
    setOptionValues(newOptions);
    setEditingOptionIndex(null);
    setEditingValue('');
  };

  // Cancel editing
  const handleCancelEdit = () => {
    if (editingOptionIndex === null) return;

    // If it was a new option (empty string), remove it
    if (optionValues[editingOptionIndex] === '') {
      setOptionValues(optionValues.filter((_, i) => i !== editingOptionIndex));
    }
    setEditingOptionIndex(null);
    setEditingValue('');
  };

  // Remove an option
  const handleRemoveOption = (index: number) => {
    setOptionValues(optionValues.filter((_, i) => i !== index));
    // If we were editing this option, clear editing state
    if (editingOptionIndex === index) {
      setEditingOptionIndex(null);
      setEditingValue('');
    } else if (editingOptionIndex !== null && editingOptionIndex > index) {
      // Adjust editing index if we removed an option before it
      setEditingOptionIndex(editingOptionIndex - 1);
    }
  };

  // Handle Enter key in editing input
  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveOption();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  // Field-specific validation errors
  const nameError = !name.trim()
    ? 'Name is required'
    : name.trim().length > 50
      ? 'Name must be 50 characters or less'
      : undefined;
  const promptError = !prompt.trim() ? 'Instructions are required' : undefined;
  // Filter out empty options (being edited) when checking for validation
  const validOptions = optionValues.filter(v => v.trim() !== '');
  const optionsError =
    type === 'options' && validOptions.length === 0
      ? 'At least one option is required'
      : undefined;
  const hasErrors = !!(nameError || promptError || optionsError);

  const resetInput = () => {
    setName('');
    setPrompt('');
    setType('text');
    setOptionValues([]);
    setEditingOptionIndex(null);
    setEditingValue('');
    setShowErrors(false);
  };

  // Check what has changed in edit mode
  const getChangeType = (): 'none' | 'nameOnly' | 'regenerate' => {
    if (!isEdit) return 'regenerate';

    const editProps = props as EditColumnModalProps;
    const nameChanged = name.trim() !== editProps.name.trim();
    const typeChanged = type !== editProps.type;
    const promptChanged = prompt.trim() !== editProps.prompt.trim();

    // Check if options changed
    const originalOptions = editProps.options?.map(o => o.value).sort() ?? [];
    const currentOptions = [...optionValues].sort();
    const optionsChanged =
      JSON.stringify(originalOptions) !== JSON.stringify(currentOptions);

    if (!nameChanged && !typeChanged && !promptChanged && !optionsChanged) {
      return 'none';
    }

    if (nameChanged && !typeChanged && !promptChanged && !optionsChanged) {
      return 'nameOnly';
    }

    return 'regenerate';
  };

  const handleSubmit = () => {
    // Check for validation errors first
    if (hasErrors) {
      setShowErrors(true);
      return;
    }

    const optionsToSubmit = type === 'options' ? getSortedOptions() : undefined;

    if (props.mode === 'create') {
      props.onCreate(name, prompt, type, optionsToSubmit);
      resetInput();
      setIsOpen(false);
      return;
    }

    if (props.mode === 'edit') {
      const changeType = getChangeType();

      // No changes - just close the dialog
      if (changeType === 'none') {
        setIsOpen(false);
        return;
      }

      // Only name changed - update without regenerating
      if (changeType === 'nameOnly') {
        props.onEdit(props.id, name, prompt, type, optionsToSubmit, true);
        setIsOpen(false);
        return;
      }

      // Type, instructions, or options changed - show confirmation dialog
      setShowConfirmDialog(true);
    }
  };

  const handleConfirmRegenerate = () => {
    if (props.mode === 'edit') {
      const optionsToSubmit =
        type === 'options' ? getSortedOptions() : undefined;
      props.onEdit(props.id, name, prompt, type, optionsToSubmit, false);
    }
    setShowConfirmDialog(false);
    setIsOpen(false);
  };

  const defaultTrigger = isEdit ? (
    <Button variant="outline" size="icon">
      <Pencil />
    </Button>
  ) : null;

  const displayedTypeMap: Record<
    ColumnType,
    { value: ColumnType; label: string; description: string; icon: ReactNode }
  > = {
    text: {
      value: 'text',
      label: 'Text',
      description:
        'All general use cases, such as document titles, summaries, etc. (e.g. SOP-QA-014)',
      icon: <Type className="h-5 w-5 shrink-0" />,
    },
    number: {
      value: 'number',
      label: 'Number',
      description: 'Measurable values (e.g. 1000, 3.14)',
      icon: <Hash className="h-5 w-5 shrink-0" />,
    },
    options: {
      value: 'options',
      label: 'Options',
      description: 'Label or group items (e.g. FDA, Yes/No)',
      icon: <Tag className="h-5 w-5 shrink-0" />,
    },
    date: {
      value: 'date',
      label: 'Date',
      description: 'Deadlines, audit dates, etc. (e.g. 6/24/2024)',
      icon: <Calendar className="h-5 w-5 shrink-0" />,
    },
    verbatim: {
      value: 'verbatim',
      label: 'Verbatim',
      description:
        'Direct quotes, such as specific regulations where wording matters',
      icon: <MessageSquareText className="h-5 w-5 shrink-0" />,
    },
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          {props.children || defaultTrigger}
        </DialogTrigger>
        <DialogContent className="max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit column' : 'New column'}</DialogTitle>
          </DialogHeader>
          <FlexCol gap="4">
            <Input
              label="Name"
              value={name}
              placeholder="Short, descriptive label"
              required
              onChange={handleNameChange}
              error={showErrors ? nameError : undefined}
            />
            <FlexCol gap="2">
              <label className="text-sm font-medium">
                <span className="mr-0.5 text-red-500">*</span>
                Type
              </label>
              <Select
                value={type}
                onValueChange={value => setType(value as ColumnType)}
              >
                <SelectTrigger>
                  <FlexRow alignItems="center" h="6" gap="2">
                    {displayedTypeMap[type].icon}
                    <Text variant="body-sm" color="primary">
                      {displayedTypeMap[type].label}
                    </Text>
                  </FlexRow>
                </SelectTrigger>
                <SelectContent className="max-w-[375px] overflow-y-auto">
                  {Object.values(displayedTypeMap).map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <FlexRow alignItems="center" gap="2">
                        {type.icon}
                        <FlexCol>
                          <Text variant="body-sm" color="primary">
                            {type.label}
                          </Text>
                          <Text
                            variant="body-xs"
                            color="secondary"
                            className="text-wrap"
                          >
                            {type.description}
                          </Text>
                        </FlexCol>
                      </FlexRow>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FlexCol>
            {type === 'options' && (
              <FlexCol gap="2">
                <label className="text-sm font-medium">Options</label>

                {/* Options list */}
                <FlexCol gap="2">
                  {optionValues.map((optionValue, index) => {
                    const isEditing = editingOptionIndex === index;

                    if (isEditing) {
                      // Editing mode: show input
                      return (
                        <FlexRow
                          key={index}
                          gap="2"
                          alignItems="center"
                          justifyContent="between"
                        >
                          <input
                            type="text"
                            value={editingValue}
                            onChange={e => setEditingValue(e.target.value)}
                            onKeyDown={handleEditKeyDown}
                            placeholder="Option name..."
                            autoFocus
                            className="flex-1 h-8 max-w-[300px] px-2 text-sm rounded-md border border-input bg-transparent focus-visible:outline-none"
                          />
                          <FlexRow gap="1">
                            <button
                              type="button"
                              onClick={handleSaveOption}
                              className="hover:bg-neutral-100 p-1.5 rounded-md"
                              title="Save"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          </FlexRow>
                        </FlexRow>
                      );
                    }

                    // Display mode: show badge (skip empty values)
                    if (!optionValue.trim()) return null;

                    return (
                      <FlexRow
                        key={index}
                        gap="2"
                        alignItems="center"
                        justifyContent="between"
                      >
                        <Badge
                          color={getColorForOption(optionValue)}
                          variant="outline"
                          className="h-7 rounded-lg gap-1 w-fit"
                        >
                          {optionValue}
                        </Badge>
                        <FlexRow gap="1">
                          <button
                            type="button"
                            onClick={() =>
                              handleStartEditOption(index, optionValue)
                            }
                            className="hover:bg-neutral-100 p-1.5 rounded-md"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveOption(index)}
                            className="hover:bg-neutral-100 p-1.5 rounded-md"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </FlexRow>
                      </FlexRow>
                    );
                  })}
                </FlexCol>

                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="w-fit"
                  onClick={handleCreateNewOption}
                  disabled={editingOptionIndex !== null}
                >
                  <Plus className="h-4 w-4" />
                  Create new option
                </Button>

                {/* Error message */}
                {showErrors && optionsError && (
                  <Text variant="body-xs" color="danger">
                    {optionsError}
                  </Text>
                )}
              </FlexCol>
            )}
            <Textarea
              label="Instructions"
              required
              rows={4}
              value={prompt}
              placeholder="Describe what values AI should generate for this column."
              onChange={handlePromptChange}
              error={showErrors ? promptError : undefined}
            />
          </FlexCol>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              onClick={handleSubmit}
              className="bg-purple-900 text-white"
              variant="dark"
            >
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog for regenerating column data */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Update column?</DialogTitle>
            <DialogDescription>
              All of the cells in this column will be overwritten with newly
              extracted data based on your updates. This action can not be
              undone. Are you sure you want to continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button variant="dark" onClick={handleConfirmRegenerate}>
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
